import React, { useState, useEffect, useMemo } from 'react';
import { studentApi } from '../../services/api';
import VideoWithSpeed from '../../components/VideoWithSpeed';

// Where "Satın Al" goes when a recommended book's publisher carries no website
// of its own. The backend already substitutes this (env PUBLISHER_STORE_URL) so
// the mobile app gets the CTA too, but the payload type still admits null and a
// purchase button that goes nowhere is worse than one that goes to the catalog.
const PUBLISHER_STORE_URL = 'https://pleakademi.com/yayinlarimiz/';

// Digikoç page — podcast + Sınava Doğru video feeds. All three lists used
// to be hardcoded arrays; now they come from /student/digikoc, which reads
// content_items rows the admin panel uploaded via /admin/content/digikoc.
// The right-hand "queue" reuses the same source — recent items across both
// kinds — so it's always the latest content rather than a placeholder.

interface DigikocItem {
  id: string;
  kind: 'podcast' | 'sinava_dogru';
  title: string;
  thumbnail_url: string | null;
  duration: string | null;
  video_url: string | null;
  created_at: string;
  /** İçerik Yükle'de opsiyonel Ders seçilirse dolu — "Tüm Dersler" filtresini besler. */
  subject_id?: number | null;
  subject_name?: string | null;
}

interface CoachVideo {
  book_id: string;
  book_title: string;
  section_label: string;
  question_number: number;
  video_url: string | null;
  thumbnail_url: string | null;
}
interface CoachPreview {
  book_id: string;
  book_title: string;
  locked_count: number;
  cover_url: string | null;
  catalog_pdf_url: string | null;
  publisher_name: string | null;
  publisher_website: string | null;
}
// A kazanım card always means one canonical MEB learning_outcomes row. PLE
// subject_units are konu metadata and never satisfy this rung. When no unique
// MEB outcome exists, the backend climbs to alt bölüm, bölüm, then kitap.
type FeedbackLevel = 'kazanim' | 'alt_bolum' | 'bolum' | 'kitap';
// When a question carries no kazanım of its own, the backend borrows the
// nearest one above it (alt bölüm → bölüm → kitap) at read time. The question
// row is never written to, so this is resolved fresh on every request.
type InheritedFrom = 'alt_bolum' | 'bolum' | 'kitap' | null;
const INHERITED_LABEL: Record<string, string> = {
  alt_bolum: 'alt bölümden',
  bolum: 'bölümden',
  kitap: 'kitaptan',
};
// Weak and strong topics now share one shape — the backend enriches both with
// performance + solution videos, so a single card renderer serves both tabs.
interface CoachTopic {
  key: string;
  level: FeedbackLevel;
  level_label: string;
  inherited_from: InheritedFrom;
  code: string | null;
  learning_outcome_id: number | null;
  title: string;
  subject_name: string | null;
  book_title: string | null;
  correct_count: number;
  wrong_count: number;
  total_count: number;
  accuracy_pct: number;
  mode: 'solution_videos' | 'lecture_guide';
  videos: CoachVideo[];
  previews: CoachPreview[];
  guide: { headline: string; message: string } | null;
}
interface CoachPlan {
  student_name: string;
  student_grade: number | null;
  has_data: boolean;
  weak: CoachTopic[];
  strong: CoachTopic[];
}

const StudentDigikoc: React.FC = () => {
  const [podcasts, setPodcasts] = useState<DigikocItem[]>([]);
  const [videos, setVideos] = useState<DigikocItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<DigikocItem | null>(null);

  // Personalized coaching panel — lazily loaded. The header button renders
  // immediately; the plan is fetched only the first time the panel is expanded
  // (not on page open), so opening Digikoç never waits on this request.
  const [plan, setPlan] = useState<CoachPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planRequested, setPlanRequested] = useState(false);
  // Solution-video lightbox (separate from the podcast/video `playing` modal
  // so the two feeds don't fight over one piece of state).
  const [coachVideo, setCoachVideo] = useState<{ title: string; url: string } | null>(null);
  // Collapsible — collapsed by default; expanding it triggers the lazy fetch.
  const [coachOpen, setCoachOpen] = useState(false);
  // Güçlü / Geliştirilecek pill toggle inside the coach panel — Güçlü opens by
  // default so the student is greeted with a win first.
  const [coachTab, setCoachTab] = useState<'strong' | 'weak'>('strong');

  const toggleCoach = () => {
    setCoachOpen((o) => !o);
    if (!planRequested) {
      setPlanRequested(true);
      setPlanLoading(true);
      studentApi.digikocCoach()
        .then((r) => setPlan(r.data))
        .catch(() => setPlan(null))
        .finally(() => setPlanLoading(false));
    }
  };

  useEffect(() => {
    setLoading(true);
    studentApi.digikoc()
      .then((r) => {
        setPodcasts(r.data.podcasts ?? []);
        setVideos(r.data.videos ?? []);
      })
      .catch((e) => setError(e?.response?.data?.error ?? e.message))
      .finally(() => setLoading(false));
  }, []);

  // Right-hand queue = latest 6 items across both kinds. Falls back gracefully
  // to an empty list when the admin hasn't uploaded any digikoç content yet.
  const queue = useMemo(() => {
    return [...podcasts, ...videos]
      .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))
      .slice(0, 6);
  }, [podcasts, videos]);

  // "Tüm Dersler" — artık gerçek bir filtre: ders atanmış videolardan seçenek
  // listesi kurulur, seçim video kolonunu daraltır (eskiden tıklanamayan süs
  // bir kutuydu).
  const [dersFilter, setDersFilter] = useState<string>('');
  const dersOptions = useMemo(
    () => Array.from(new Set(videos.map((v) => v.subject_name).filter(Boolean))) as string[],
    [videos],
  );
  const filteredVideos = dersFilter ? videos.filter((v) => v.subject_name === dersFilter) : videos;
  // Aynı filtre podcast kolonu için de (bağımsız seçim).
  const [podFilter, setPodFilter] = useState<string>('');
  const podOptions = useMemo(
    () => Array.from(new Set(podcasts.map((p) => p.subject_name).filter(Boolean))) as string[],
    [podcasts],
  );
  const filteredPodcasts = podFilter ? podcasts.filter((p) => p.subject_name === podFilter) : podcasts;

  const renderTopicCard = (w: CoachTopic, tone: 'weak' | 'strong') => {
    const strong = tone === 'strong';
    const color = strong ? '#059669' : w.accuracy_pct < 50 ? '#dc2626' : '#f59e0b';
    const shell = strong
      ? 'bg-emerald-50 border-emerald-100'
      : w.accuracy_pct < 50 ? 'bg-red-50 border-red-100' : 'bg-amber-50 border-amber-100';
    return (
      <div key={w.key} className={`rounded-[12px] border ${shell} p-3`}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <p className="text-[13px] xl:text-[15px] font-bold text-[#2b2b2b] truncate">{w.title}</p>
              {/* Say which rung this is. Without it a bölüm reads as a kazanım,
                  which is a different (and much more specific) promise. */}
              {w.level !== 'kazanim' && (
                <span className="flex-shrink-0 text-[9px] xl:text-[10px] font-bold uppercase tracking-wide text-gray-500 bg-white/80 border border-gray-200 rounded-full px-1.5 py-0.5">
                  {w.level_label}
                </span>
              )}
              {/* A kazanım the question did not carry itself, borrowed from a
                  parent scope. Saying so keeps the card honest without
                  demoting it — an inherited kazanım IS the question's kazanım,
                  it just isn't stored on the row. */}
              {w.level === 'kazanim' && w.inherited_from && (
                <span
                  title={`Bu soruda kazanım tanımlı değil; ${INHERITED_LABEL[w.inherited_from]} devralındı.`}
                  className="flex-shrink-0 text-[9px] xl:text-[10px] font-semibold text-gray-400 border border-dashed border-gray-300 rounded-full px-1.5 py-0.5"
                >
                  {INHERITED_LABEL[w.inherited_from]}
                </span>
              )}
            </div>
            {/* The book is load-bearing, not decoration: section labels repeat
                across books (this student has 56 labels over 101 book+label
                pairs), so "Kazanım Denemesi 4" appears twice — %0 in Fizik and
                %92 in Coğrafya. Without the book the two cards are identical. */}
            <p className="text-[11px] xl:text-[12px] text-gray-500 truncate">
              {[w.subject_name, w.book_title].filter(Boolean).join(' · ')}
              {(w.subject_name || w.book_title) ? ' · ' : ''}
              {w.correct_count}/{w.total_count} doğru
            </p>
          </div>
          <span className="flex-shrink-0 text-[15px] xl:text-[18px] font-extrabold" style={{ color }}>%{w.accuracy_pct}</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/70 overflow-hidden mb-3">
          <div className="h-full rounded-full" style={{ width: `${w.accuracy_pct}%`, background: color }} />
        </div>

        {/* Critical (<50%) framing: needs foundational lecture content (not in S3 yet). */}
        {w.guide && (
          <div className="bg-white/80 rounded-[8px] border border-red-100 p-2.5 mb-2.5">
            <span className="inline-flex items-center gap-1 bg-red-100 text-red-600 text-[10px] font-bold rounded-full px-2 py-0.5 mb-1.5">📚 KONU ANLATIMI YAKINDA</span>
            <p className="text-[11px] xl:text-[13px] text-gray-600 leading-relaxed">{w.guide.message}</p>
          </div>
        )}

        {/* Solution videos from books İdil OWNS — playable now. */}
        {w.videos.length > 0 && (
          <div className="mb-2.5">
            <p className="text-[11px] xl:text-[12px] font-semibold text-gray-600 mb-2">
              🎬 {strong ? 'Pekiştirme videoları' : 'Çözümlü soru videoları'} <span className="text-emerald-600 font-bold">· Kitaplığında</span>
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {w.videos.map((v, i) => (
                <button
                  key={i}
                  onClick={() => v.video_url && setCoachVideo({ title: `${w.title} — ${v.book_title} · Soru ${v.question_number}`, url: v.video_url })}
                  className="flex-shrink-0 w-[120px] xl:w-[150px] group text-left"
                >
                  <div className="relative w-full rounded-[8px] overflow-hidden bg-gray-200 border border-gray-100" style={{ aspectRatio: '16/10' }}>
                    {v.thumbnail_url
                      ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-gray-100" />}
                    <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                        <svg width="10" height="12" viewBox="0 0 10 12" fill="#3973bf"><path d="M0 0l10 6-10 6z" /></svg>
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] xl:text-[11px] text-gray-600 mt-1 truncate">{v.book_title}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* B9: in-grade books the student does NOT own. The block above lists
            videos from books she DOES own, badged "· Kitaplığında" — so this is
            the gap in that shelf, and it's framed as one: an object to pick up,
            not a wall to bounce off. Satın Al → publisher store (falls back to
            the platform store server-side), İncele → catalog sample PDF. */}
        {w.previews.length > 0 && (
          <div>
            <p className="flex items-center gap-1.5 mb-2">
              <span className="text-[10px] xl:text-[11px] font-bold uppercase tracking-[0.14em] text-[#8b6a1f] bg-[#f5b73d]/25 rounded-full px-2 py-0.5">
                Kitaplığında yok
              </span>
              <span className="text-[11px] xl:text-[12px] font-semibold text-gray-500">Digikoç önerisi</span>
            </p>
            <div className="space-y-2.5">
              {w.previews.map((p, i) => (
                <div key={i} className="rec-card rec-shelf rounded-[14px] p-3 xl:p-4">
                  <div className="flex items-start gap-3.5 xl:gap-4">
                    {/* The book, standing on a shelf ledge. */}
                    <div className="flex-shrink-0 w-[58px] xl:w-[68px]">
                      <div className="rec-book relative w-full rounded-[3px] overflow-hidden shadow-[0_6px_14px_-6px_rgba(0,0,0,0.7)]" style={{ aspectRatio: '3/4' }}>
                        {p.cover_url
                          ? <img src={p.cover_url} alt="" className="w-full h-full object-cover" />
                          : <div className="w-full h-full bg-[#3a2c63]" />}
                        {/* Spine edge — what turns a flat thumbnail into a book. */}
                        <div className="rec-book__spine absolute inset-y-0 left-0 w-[7px]" />
                      </div>
                      <div className="rec-book__shelf h-[2px] mt-1.5 rounded-full" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="rec-title text-[14px] xl:text-[16px] text-white leading-snug">{p.book_title}</p>
                      {p.publisher_name && (
                        <p className="text-[10px] xl:text-[11px] font-semibold uppercase tracking-[0.12em] text-[#b9afd6] mt-1">{p.publisher_name}</p>
                      )}

                      {/* The count is the product. It was 11px grey text behind a
                          padlock emoji; it's the reason to buy, so it gets the size. */}
                      <div className="flex items-end gap-2 mt-3">
                        <span className="rec-count text-[30px] xl:text-[38px]">{p.locked_count}</span>
                        <span className="text-[11px] xl:text-[12px] text-[#b9afd6] leading-tight pb-1">
                          çözümlü<br />soru videosu
                        </span>
                      </div>
                      {/* Says the one thing the count doesn't: these videos are
                          for THIS kazanım, not the book at large. Nested under a
                          weak topic, that's the whole reason the card is here. */}
                      <p className="text-[11px] xl:text-[12px] text-[#d8d0ec] mt-2">
                        Tamamı bu konunun soruları.
                      </p>

                      <div className="flex flex-wrap items-center gap-2 mt-3.5">
                        <a
                          href={p.publisher_website ?? PUBLISHER_STORE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rec-buy inline-flex items-center gap-1.5 rounded-full pl-3.5 pr-3 py-2 text-[12px] xl:text-[13px] font-bold text-white"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="9" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" />
                            <path d="M2 3h2.2l2.3 11.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 2-1.55L21 7H5.5" />
                          </svg>
                          Satın Al
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="opacity-70" aria-hidden="true">
                            <path d="M7 17L17 7" /><path d="M8 7h9v9" />
                          </svg>
                        </a>
                        {p.catalog_pdf_url && (
                          <a
                            href={p.catalog_pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rec-ghost inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12px] xl:text-[13px] font-semibold"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M4 19.5V5a2 2 0 0 1 2-2h11a1 1 0 0 1 1 1v15" /><path d="M6 17h12" /><path d="M6 21h12a2 2 0 0 1-2-2v-2" />
                            </svg>
                            Örnek sayfalar
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Nothing to show for this topic yet — positive framing for strong. */}
        {w.videos.length === 0 && w.previews.length === 0 && !w.guide && (
          <p className="text-[11px] text-gray-400">
            {strong ? 'Bu konuda başarılısın — pekiştirme videoları yakında eklenecek.' : 'Bu konu için içerik yakında eklenecek.'}
          </p>
        )}
      </div>
    );
  };

  const renderItemCard = (it: DigikocItem, accent: 'pink' | 'blue') => {
    const ring = accent === 'pink' ? 'hover:border-[#e5599b]' : 'hover:border-[#3973bf]';
    const chip = accent === 'pink' ? 'bg-[#e5599b]/10 text-[#e5599b]' : 'bg-[#3973bf]/10 text-[#3973bf]';
    const playBg = accent === 'pink' ? 'bg-[#e5599b]' : 'bg-[#3973bf]';
    return (
      <button
        key={it.id}
        onClick={() => it.video_url && setPlaying(it)}
        className={`group w-full text-left flex items-center gap-3 rounded-[12px] p-2.5 border border-gray-100 bg-white ${ring} hover:shadow-sm transition-all`}
      >
        {/* Thumbnail with hover play affordance — fixed size so a long title
            never stretches the row (min-w-0 on the text side does the rest). */}
        <div className="relative w-14 h-14 xl:w-16 xl:h-16 rounded-[10px] overflow-hidden bg-gray-100 flex-shrink-0">
          {it.thumbnail_url
            ? <img src={it.thumbnail_url} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full bg-gray-100" />}
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/25 transition-colors">
            <span className={`w-7 h-7 rounded-full ${playBg} shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}>
              <svg width="9" height="11" viewBox="0 0 10 12" fill="white"><path d="M0 0l10 6-10 6z" /></svg>
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] xl:text-[15px] font-semibold text-[#2b2b2b] leading-snug line-clamp-2">{it.title}</p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {it.subject_name && (
              <span className={`text-[10px] xl:text-[11px] font-semibold rounded-full px-2 py-0.5 ${chip}`}>{it.subject_name}</span>
            )}
            {it.duration && (
              <span className="inline-flex items-center gap-1 text-[10px] xl:text-[12px] text-gray-500">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                {it.duration}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div>
      <div className="mb-3">
        <h1 className="text-[18px] xl:text-[24px] font-semibold text-[#2b2b2b]">Digikoç</h1>
        <p className="text-[12px] xl:text-[14px] text-gray-500 mt-0.5">Kişisel koçun, çözümlü videolar ve sınava hazırlık serileri bir arada.</p>
      </div>

      {/* Sana Özel Koçluk — the page's signature CTA. An animated wavy brand
          gradient with twinkling sparkles + a floating mascot pulls the eye here
          first. Clicking expands the personalized coaching (lazy-fetched). */}
      <div className="mb-3 rounded-[16px] overflow-hidden shadow-md border border-gray-100 bg-white">
        <button
          type="button"
          onClick={toggleCoach}
          aria-expanded={coachOpen}
          className="dk-hero relative w-full overflow-hidden px-4 py-4 xl:px-7 xl:py-6 flex items-center gap-3 xl:gap-5 text-left cursor-pointer transition-transform hover:scale-[1.004]"
        >
          {/* twinkling sparkles */}
          {[
            { top: '16%', left: '34%', s: 10, d: '0s' },
            { top: '64%', left: '28%', s: 8, d: '0.5s' },
            { top: '22%', left: '66%', s: 13, d: '1s' },
            { top: '70%', left: '82%', s: 9, d: '1.4s' },
            { top: '46%', left: '50%', s: 7, d: '0.8s' },
          ].map((sp, i) => (
            <svg key={i} className="dk-sparkle absolute text-white/90 pointer-events-none" width={sp.s} height={sp.s} viewBox="0 0 24 24" fill="currentColor" style={{ top: sp.top, left: sp.left, animationDelay: sp.d }}>
              <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
            </svg>
          ))}
          {/* floating mascot */}
          <div className="relative z-10 min-w-0 flex-1">
            <p className="text-white font-extrabold text-[19px] xl:text-[28px] leading-tight drop-shadow-sm">Sana Özel Koçluk</p>
            <p className="text-white/90 text-[11px] xl:text-[15px] leading-snug mt-0.5">
              Güçlü ve geliştirmen gereken konuların. Performansına özel çözümlü videolarla.
            </p>
          </div>
          <div className="relative z-10 flex-shrink-0 flex items-center gap-2">
            <span className="hidden xl:inline text-white font-bold text-[14px]">{coachOpen ? 'Kapat' : 'Aç'}</span>
            <span className="w-9 h-9 xl:w-11 xl:h-11 rounded-full bg-white/25 flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4"
                className={`transition-transform duration-200 ${coachOpen ? '' : '-rotate-90'}`}>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </div>
        </button>

        {coachOpen && (
          <div className="p-3 xl:p-4">
            {planLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : plan && plan.has_data ? (
              <div>
                {/* Güçlü / Geliştirilecek pill toggle — Güçlü opens by default. */}
                <div className="inline-flex items-center gap-1 bg-gray-100 rounded-full p-1 mb-3">
                  <button
                    onClick={() => setCoachTab('strong')}
                    className={`text-[12px] xl:text-[14px] font-semibold px-4 py-2 rounded-full transition-colors flex items-center gap-1.5 ${coachTab === 'strong' ? 'bg-emerald-500 text-white shadow-sm' : 'text-gray-600 hover:text-emerald-600'}`}
                  >
                    Güçlü Konuların
                    <span className={`text-[10px] xl:text-[11px] font-bold rounded-full px-1.5 ${coachTab === 'strong' ? 'bg-white/25 text-white' : 'bg-emerald-100 text-emerald-700'}`}>{plan.strong.length}</span>
                  </button>
                  <button
                    onClick={() => setCoachTab('weak')}
                    className={`text-[12px] xl:text-[14px] font-semibold px-4 py-2 rounded-full transition-colors flex items-center gap-1.5 ${coachTab === 'weak' ? 'bg-[#e5599b] text-white shadow-sm' : 'text-gray-600 hover:text-[#e5599b]'}`}
                  >
                    Geliştirmen Gereken
                    <span className={`text-[10px] xl:text-[11px] font-bold rounded-full px-1.5 ${coachTab === 'weak' ? 'bg-white/25 text-white' : 'bg-pink-100 text-[#e5599b]'}`}>{plan.weak.length}</span>
                  </button>
                </div>

                {coachTab === 'strong' ? (
                  plan.strong.length === 0 ? (
                    <div className="flex items-center gap-3 xl:gap-4 rounded-[14px] border border-emerald-100 bg-emerald-50 p-4">
                      <img src="/a-digikoc-mascot.png" alt="" className="w-14 h-14 xl:w-16 xl:h-16 object-contain flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[14px] xl:text-[16px] font-bold text-emerald-700">Güçlü konu rozetlerin yolda! 🚀</p>
                        <p className="text-[12px] xl:text-[13px] text-emerald-600/80 mt-0.5">Çözdükçe en iyi olduğun konular burada belirecek. Çözmeye devam et!</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Celebratory well-done banner — greets the student with a win. */}
                      <div className="relative overflow-hidden flex items-center gap-3 xl:gap-4 rounded-[14px] p-4 bg-gradient-to-r from-emerald-500 to-teal-500">
                        <img src="/a-register-success-mascot.png" alt="" className="dk-float w-20 h-2 xl:w-20 xl:h-20 object-contain flex-shrink-0 drop-shadow-lg" />
                        <div className="min-w-0">
                          <p className="text-white font-extrabold text-[16px] xl:text-[20px] leading-tight">Aferin{plan.student_name ? `, ${plan.student_name}` : ''}!</p>
                          <p className="text-white/90 text-[12px] xl:text-[14px] mt-0.5">{plan.strong.length} konuda güçlüsün. Zirvede kalmak için çözümlü videolarla pekiştir.</p>
                        </div>
                      </div>
                      <div className="space-y-2.5">
                        {plan.strong.map((s) => renderTopicCard(s, 'strong'))}
                      </div>
                    </div>
                  )
                ) : (
                  plan.weak.length === 0 ? (
                    <div className="flex items-center gap-3 xl:gap-4 rounded-[14px] border border-emerald-100 bg-emerald-50 p-4">
                      <img src="/a-register-success-mascot.png" alt="" className="w-14 h-14 xl:w-16 xl:h-16 object-contain flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[14px] xl:text-[16px] font-bold text-emerald-700">Harika! Şu an geliştirmen gereken bir konu yok 🎉</p>
                        <p className="text-[12px] xl:text-[13px] text-emerald-600/80 mt-0.5">Böyle devam! Yeni sorular çözdükçe buradan takip edebilirsin.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Motivating (not disappointing) banner — smiling mascot,
                          warm gradient, "let's work on these" framing. */}
                      <div className="relative overflow-hidden flex items-center gap-3 xl:gap-4 rounded-[14px] p-4 bg-gradient-to-r from-[#e5599b] to-[#f59e0b]">
                        <img src="/a-digikoc-mascot.png" alt="" className="dk-float w-16 h-16 xl:w-20 xl:h-20 object-contain flex-shrink-0 drop-shadow-lg" />
                        <div className="min-w-0">
                          <p className="text-white font-extrabold text-[16px] xl:text-[20px] leading-tight">Hadi bunları da güçlendirelim!</p>
                          <p className="text-white/90 text-[12px] xl:text-[14px] mt-0.5">Bu {plan.weak.length} konuya biraz daha çalış. Çözümlü videolarla adım adım hallederiz.</p>
                        </div>
                      </div>
                      <div className="space-y-2.5">
                        {plan.weak.map((w) => renderTopicCard(w, 'weak'))}
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : plan && !plan.has_data ? (
              <p className="text-[12px] xl:text-[13px] text-gray-500 text-center py-2">
                Konu bazlı koçluk için önce kitaplardan soru çözmelisin. Çözdükçe zayıf ve güçlü konuların burada görünecek.
              </p>
            ) : (
              <p className="text-center text-red-400 py-4 text-sm">Koçluk verisi yüklenemedi.</p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-3 mb-3" style={{ aspectRatio: '1493/381' }}>
        <div className="w-[56.5%] rounded-[16px] overflow-hidden">
          <img src="/a-dk-banner-full.png" alt="Digikoç" className="w-full h-full object-cover" />
        </div>
        <div className="w-[43.5%] rounded-[16px] overflow-hidden">
          {/* Statik player görseli yerine gerçek içerik: İzleme/Dinleme
              Sırası'nın ilk öğesi — tıklayınca oynatıcı açılır. Sıra boşken
              eski görsel geri gelir. */}
          {queue.length > 0 && queue[0].video_url ? (
            <button
              onClick={() => setPlaying(queue[0])}
              title={queue[0].title}
              className="relative block w-full h-full cursor-pointer group text-left overflow-hidden"
            >
              {/* absolute fill: kapak görselinin doğal boyutu satırı şişirmesin
                  (dev dikey kapak tüm sayfayı büyütüyordu) — akıştan çıkar. */}
              <img
                src={queue[0].thumbnail_url || '/a-dk-video-full.png'}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4">
                <div className="w-11 h-11 xl:w-14 xl:h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  <svg width="16" height="18" viewBox="0 0 10 12" fill="#3973bf" className="ml-0.5"><path d="M0 0l10 6-10 6z"/></svg>
                </div>
                <p className="max-w-full truncate text-center text-white text-[12px] xl:text-[15px] font-semibold drop-shadow">
                  {queue[0].title}
                </p>
              </div>
            </button>
          ) : (
            <img src="/a-dk-video-full.png" alt="Video" className="w-full h-full object-cover" />
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-3">
        {/* Podcast + Sınava Doğru feeds — deliberately calm, flat cards (no
            gradient/animation) so the animated hero above stays the one focal
            point. Real styled headers replace the old PNG tabs, and every flex
            child carries min-w-0 so long titles clamp instead of overflowing. */}
        <div className="flex-1 min-w-0 rounded-[16px] border border-gray-100 bg-white overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <p className="text-center text-red-400 py-8">{error}</p>
          ) : (
            <div className="flex flex-col lg:flex-row lg:divide-x divide-gray-100">
              {/* Digikoç Podcastler */}
              <div className="flex-1 min-w-0 p-3 xl:p-4 border-b lg:border-b-0 border-gray-100">
                <div className="flex items-center gap-2 mb-3 min-w-0">
                  <span className="w-8 h-8 rounded-full bg-[#e5599b]/10 flex items-center justify-center flex-shrink-0">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#e5599b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0014 0" /><line x1="12" y1="19" x2="12" y2="22" /></svg>
                  </span>
                  <p className="text-[14px] xl:text-[16px] font-bold text-[#2b2b2b] truncate">Digikoç Podcastler</p>
                </div>
                <select
                  value={podFilter}
                  onChange={(e) => setPodFilter(e.target.value)}
                  className="w-full bg-gray-50 rounded-[10px] px-3 py-2 mb-3 border border-gray-200 text-[13px] xl:text-[14px] font-medium text-[#2b2b2b] cursor-pointer focus:outline-none focus:border-[#e5599b]"
                >
                  <option value="">Tüm Dersler</option>
                  {podOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <div className="space-y-2">
                  {podcasts.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-8">Henüz podcast yüklenmedi.</p>
                  ) : filteredPodcasts.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-8">Bu derste podcast yok.</p>
                  ) : (
                    filteredPodcasts.map((t) => renderItemCard(t, 'pink'))
                  )}
                </div>
              </div>
              {/* Sınava Doğru Video Serisi */}
              <div className="flex-1 min-w-0 p-3 xl:p-4">
                <div className="flex items-center gap-2 mb-3 min-w-0">
                  <span className="w-8 h-8 rounded-full bg-[#3973bf]/10 flex items-center justify-center flex-shrink-0">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="#3973bf"><path d="M8 5v14l11-7z" /></svg>
                  </span>
                  <p className="text-[14px] xl:text-[16px] font-bold text-[#2b2b2b] truncate">Sınava Doğru Video Serisi</p>
                </div>
                <select
                  value={dersFilter}
                  onChange={(e) => setDersFilter(e.target.value)}
                  className="w-full bg-gray-50 rounded-[10px] px-3 py-2 mb-3 border border-gray-200 text-[13px] xl:text-[14px] font-medium text-[#2b2b2b] cursor-pointer focus:outline-none focus:border-[#3973bf]"
                >
                  <option value="">Tüm Dersler</option>
                  {dersOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <div className="space-y-2">
                  {videos.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-8">Henüz video yüklenmedi.</p>
                  ) : filteredVideos.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-8">Bu derste video yok.</p>
                  ) : (
                    filteredVideos.map((v) => renderItemCard(v, 'blue'))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* İzleme / Dinleme Sırası — clean styled header replaces the PNG. */}
        <div className="w-full lg:w-[340px] flex-shrink-0 hidden lg:block">
          <div className="rounded-[16px] border border-gray-100 bg-white overflow-hidden h-full">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
              <span className="w-8 h-8 rounded-full bg-[#59e56e]/15 flex items-center justify-center flex-shrink-0">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2fae4e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><circle cx="3.5" cy="6" r="1" /><circle cx="3.5" cy="12" r="1" /><circle cx="3.5" cy="18" r="1" /></svg>
              </span>
              <p className="text-[14px] xl:text-[16px] font-bold text-[#2b2b2b]">İzleme / Dinleme Sırası</p>
            </div>
            <div className="p-3 xl:p-4 space-y-2 min-h-[200px]">
              {loading ? (
                <p className="text-gray-400 text-sm text-center py-8">Yükleniyor…</p>
              ) : queue.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">Yakında eklenecek.</p>
              ) : (
                queue.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => q.video_url && setPlaying(q)}
                    className="group w-full text-left flex items-center gap-2.5 rounded-[10px] p-2.5 border border-gray-100 bg-white hover:border-[#59e56e] hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-full bg-[#59e56e]/15 group-hover:bg-[#59e56e] flex items-center justify-center flex-shrink-0 transition-colors">
                      <svg width="9" height="11" viewBox="0 0 10 12" className="fill-[#2fae4e] group-hover:fill-white transition-colors"><path d="M0 0l10 6-10 6z" /></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] xl:text-[14px] font-semibold text-[#2b2b2b] truncate">{q.title}</p>
                      {q.duration && <p className="text-[10px] xl:text-[12px] text-gray-500">{q.duration}</p>}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {playing && playing.video_url && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4" onClick={() => setPlaying(null)}>
          <div className="relative w-full max-w-[900px]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPlaying(null)} className="absolute -top-10 right-0 text-white text-xl">✕ Kapat</button>
            <VideoWithSpeed autoPlay src={playing.video_url} className="w-full rounded-[12px] bg-black" />
            <p className="text-white text-center mt-3">{playing.title}</p>
          </div>
        </div>
      )}

      {coachVideo && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4" onClick={() => setCoachVideo(null)}>
          <div className="relative w-full max-w-[900px]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setCoachVideo(null)} className="absolute -top-10 right-0 text-white text-xl">✕ Kapat</button>
            <VideoWithSpeed autoPlay src={coachVideo.url} className="w-full rounded-[12px] bg-black" />
            <p className="text-white text-center mt-3">{coachVideo.title}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDigikoc;
